/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, {
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Breadcrumbs,
  Link,
  Tooltip,
  Container
} from "@mui/material";
import { fetchSubmissionDetails } from "../../Services/redux/Application-details/ApplicationDetailsActions";
import { fetchApplicationDetails } from "../../Services/redux/appId-details/AppIdDeatilsActions";
import "./InAppProducts.css";
import Topheader from "../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ApplicationDetailsErrorDialog from "../../Components/ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import APIManager from "../../Services/ApiManager/ApiManager";
import noProductsImage from "../../assets/images/app-list-blank.svg";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";
import RepeatIcon from "@mui/icons-material/Repeat";
import EditIcon from "@mui/icons-material/Edit";
import Grid from "@mui/material/Grid2";
import { LoadingBackdrop } from "../../Components/LoadingBackdrop/LoadingBackdrop";

const monthNames = [
  "Jan", "Feb", "March", "Apr", "May", "June",
  "July", "August", "Sept", "Oct", "Nov", "Dec",
];

const frequencyUnitConverter = {
  "1-DAY": "Daily",
  "1-MONTH": "Monthly",
  "3-MONTH": "Quarterly",
  "6-MONTH": "Half yearly",
  "1-YEAR": "Yearly",
};

function createData({ type, id, productId, price, description, trialPeriod, noOfPlans, lastModified, plans }) {
  return { type, id, productId, price, description, trialPeriod, noOfPlans, lastModified, plans };
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
  { id: "productId", numeric: false, disablePadding: false, label: "Product Id" },
  { id: "price", numeric: false, disablePadding: false, label: "Price" },
  { id: "description", numeric: false, disablePadding: false, label: "Description" },
  { id: "trialPeriod", numeric: false, disablePadding: false, label: "Trial Period" },
  { id: "noOfPlans", numeric: true, disablePadding: false, label: "No. Of Plans" },
  { id: "lastModified", numeric: true, disablePadding: false, label: "Last Modified" },
];

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead className="tableHeaderCls">
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ fontWeight: "bold", padding: "8px 10px" }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc"
                    ? "sorted descending"
                    : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

function Row({ row, goToEditPage }) {
  const [open, setOpen] = React.useState(false);
  return (
    <React.Fragment>
      <TableRow hover tabIndex={-1} key={row.id} sx={{ cursor: "pointer", height: "32px" }}>
        <TableCell
          component="th"
          scope="row"
          padding="normal"
          align="left"
          sx={{ fontWeight: "bold", width: "15%", fontSize: "smaller", padding: "6px 10px" }}
        >
          <Tooltip title={row.productId}>
            {row.productId.length > 8
              ? `${row.productId.substring(0, 8)}...`
              : row.productId}
          </Tooltip>{" "}
          {row.type === "recurring" && (
            <RepeatIcon
              fontSize={"small"}
              sx={{
                marginLeft: "4%",
                marginBottom: "-4%",
              }}
            />
          )}
        </TableCell>
        <TableCell align="left" sx={{ width: "12%", padding: "6px 10px" }}>
          <span style={{ color: "#717273" }}>
            {row.type === "oneTime" && "USD"}
          </span>{" "}
          {row.price}
        </TableCell>
        <TableCell align="left" sx={{ width: "15%", padding: "6px 10px" }}>
          <Tooltip title={row.description}>
            {row.description.length > 12
              ? `${row.description.substring(0, 12)}...`
              : row.description}
          </Tooltip>
        </TableCell>
        <TableCell align="left" sx={{ width: "15%", padding: "6px 10px" }}>
          {row.trialPeriod}
        </TableCell>
        <TableCell align="left" sx={{ width: "15%", padding: "6px 10px" }}>
          {row.noOfPlans}
        </TableCell>
        <TableCell align="left" padding="none" sx={{ paddingLeft: "2%", padding: "6px 10px" }}>
          <Grid container spacing={1} alignItems="center">
            <Grid size={8}>{`${new Date(row.lastModified).getDate()} ${
              monthNames[new Date(row.lastModified).getMonth()]
            } ${new Date(row.lastModified).getFullYear()}`}</Grid>
            <Grid size={4} sx={{ display: "flex", justifyContent: "flex-start" }}>
              <span className="icons-cont">
                <IconButton
                  aria-label="expand row"
                  size="small"
                  sx={{ transform: "scale(.8)" }}
                  onClick={() => goToEditPage(row.id, row.type)}
                >
                  <EditIcon />
                </IconButton>
                {row.plans && row.plans.length > 0 && (
                  <IconButton
                    aria-label="expand row"
                    size="small"
                    sx={{ transform: "scale(.8)" }}
                    onClick={() => setOpen(!open)}
                  >
                    {open ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                )}
              </span>
            </Grid>
          </Grid>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0, padding: 0 }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f3f3f3" }}>
                    <TableCell
                      sx={{ fontWeight: "600", fontSize: "12px", lineHeight: "16px" }}
                    >
                      S. No
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "600", fontSize: "12px", lineHeight: "16px" }}
                    >
                      Plan Name
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: "600", fontSize: "12px", lineHeight: "16px" }}
                    >
                      Subscription Frequency
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: "600", fontSize: "12px", lineHeight: "16px" }}
                    >
                      Price
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: "600", fontSize: "12px", lineHeight: "16px" }}
                    >
                      Offers
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.plans?.map((plan, index) => (
                      <TableRow key={plan.id}>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: "600", fontSize: "14px", lineHeight: "16px" }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "600", fontSize: "14px", lineHeight: "16px"  }}>
                          {plan.name}
                        </TableCell>
                        <TableCell align="left" sx={{ fontWeight: "600", fontSize: "14px", lineHeight: "16px"  }}>
                          {
                            frequencyUnitConverter[
                              plan.recurringFrequency +
                                "-" +
                                plan.recurringFrequencyUnit
                            ]
                          }
                        </TableCell>
                        <TableCell align="left" sx={{ fontWeight: "600", fontSize: "14px", lineHeight: "16px"  }}>
                          USD {plan.basePrice.price.toFixed(2)}
                        </TableCell>
                        <TableCell align="left" sx={{ fontWeight: "600", fontSize: "14px", lineHeight: "16px"  }}>
                          0
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    productId: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    description: PropTypes.string.isRequired,
    trialPeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    noOfPlans: PropTypes.number.isRequired,
    lastModified: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    plans: PropTypes.array,
  }).isRequired,
  goToEditPage: PropTypes.func.isRequired,
};

function InAppProducts(props) {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const teamId = location.state.teamId;
  const [packageName, setPackageName] = useState("");
  const [sellerId, setSellerId] = useState("");
  const { loading, submissionDetails } = useSelector(
    (state) => state.submissionDetails
  );
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("lastModified");
  const [rows, setRows] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows]
  );

  const errors = useSelector((state) => state.errors);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(true);
  const [productList, setProductList] = useState([]);

  useEffect(() => {
    if (appData?.submissions?.length > 0) {
      dispatch(fetchSubmissionDetails(appId, appData.submissions[0].id));
    }
  }, [appData]);
  useEffect(() => {
    dispatch(fetchApplicationDetails(appId));
  }, [appId]);
  useEffect(() => {
    if (submissionDetails && Object.keys(submissionDetails).length > 0) {
      if (submissionDetails?.binary?.packageName && !packageName) {
        setPackageName(submissionDetails.binary.packageName);
        getUserDetails(submissionDetails.binary.packageName);
      } else {
        // For DRAFT status or when binary is not available, stop loading
        setLoader(false);
      }
    }
  }, [submissionDetails, packageName]);

  const getUserDetails = (packageName) => {
    APIManager.getUserInfo().then((json) => {
      if (json.messages?.[0]?.key === "success.key") {
        getAllInApp(json?.results[0].id, packageName);
        setSellerId(json?.results[0].id);
      } else {
        setLoader(false);
      }
    });
  };

  const getAllInApp = (sellerId, packageName) => {
    APIManager.getAllInApp(sellerId, packageName).then((json) => {
      setProductList(json);
      setLoader(false);
      if (json.length > 0) {
        formTableWithData(json);
      }
    });
  };

  const formTableWithData = (products) => {
    let allRows = [];
    products.forEach((product) => {
      if (product.oneTimePlan === null) {
        allRows.push(
          createData({
            type: "recurring",
            id: product.product.id,
            productId: product.product.productId,
            price: "-",
            description: product.product.description,
            trialPeriod: product.trialPrice?.trialPeriod,
            noOfPlans: product.recurringPlans.length,
            lastModified: product.product.lastUpdated,
            plans: product.recurringPlans,
          })
        );
      } else {
        allRows.push(
          createData({
            type: "oneTime",
            id: product.product.id,
            productId: product.product.productId,
            price: product.oneTimePlan?.basePrice?.price.toFixed(2),
            description: product.product.description,
            trialPeriod: product.trialPrice?.trialPeriod,
            noOfPlans: 1,
            lastModified: product.product.lastUpdated,
            plans: [],
          })
        );
      }
    });
    setRows(allRows);
  };

  const backToApplications = () => {
    navigate(-1);
  };


  const goToEditPage = (productId, type) => {
    if (type === "recurring") {
      navigate(
        `/ui/developer/applications/${appId}/edit-recurring-product/${productId}`,
        {
          state: { teamId: teamId, sellerId: sellerId },
        }
      );
    } else {
      navigate(
        `/ui/developer/applications/${appId}/edit-one-time-product/${productId}`,
        {
          state: { teamId: teamId, sellerId: sellerId },
        }
      );
    }
  };

  return (
    <>
      <LoadingBackdrop open={loader} />

      <Topheader />
      {
        <Container maxWidth="lg">
                  <Grid container>
                    <Grid items xs={12} sm={12} md={12} lg={12}></Grid>
        <div
          className="container"
          style={
            submissionDetails &&
            Object.keys(submissionDetails).length > 0 &&
            submissionDetails.status === "DRAFT"
              ? {
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  width: "1187px"
                }
              : { display: "flex", flexDirection: "row", width: "1187px" }
          }
        >
          <div
            className="side-menu"
            style={
              submissionDetails &&
              Object.keys(submissionDetails).length > 0 &&
              submissionDetails.status !== "DRAFT"
                ? { marginTop: "69px" }
                : { marginTop: "0px" }
            }
          >
            <button
              className="arrow-back"
              onClick={backToApplications}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
            >
              <div>
                <ArrowBackIcon style={{ color: " #717273" }} />
              </div>
              <div className="my-apps">App Overview</div>
            </button>
            {submissionDetails &&
              Object.keys(submissionDetails).length > 0 &&
              submissionDetails.status !== "DRAFT" &&
              appData &&
              Object.keys(appData).length > 0 && (
                <ApplicationDetailsSideMenu
                  appData={appData}
                  isMonetized={submissionDetails.isMonetized}
                />
              )}
          </div>
          <div
            className="submission-container"
            style={
              submissionDetails &&
              Object.keys(submissionDetails).length > 0 &&
              submissionDetails.status !== "DRAFT"
                ? { width: "94%", marginTop: "42px", marginLeft: "70px" }
                : { marginLeft: "0px", marginTop: "30px" }
            }
          >
            <section className="listing-header-container">
              <div style={{ marginLeft: "16%", marginBottom: "15px" }}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link
                    underline="hover"
                    onClick={backToApplications}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowBackIcon
                      sx={{ mr: 0.5, marginRight: "19px", color: "#b3b3b3" }}
                      fontSize="inherit"
                    />
                    {submissionDetails &&
                      Object.keys(submissionDetails).length > 0 && (
                        <>{submissionDetails.descriptions[0].applicationName}</>
                      )}
                  </Link>
                  <Link
                    underline="none"
                    sx={{ fontWeight: "500", color: "#2a2c2d" }}
                  >
                    In App Products
                  </Link>
                </Breadcrumbs>
              </div>
              {submissionDetails &&
                Object.keys(submissionDetails).length > 0 && (
                  <div className="container-header-icons">
                    <div className="app-icon" style={{ marginLeft: "16%" }}>
                      <div className="icon-name">
                        <img
                          src={submissionDetails.icon.url}
                          alt={submissionDetails.fileName}
                          width={50}
                          height={50}
                        />
                        {
                          <div className="name-status">
                            <p
                              className="application-name"
                              style={{ marginTop: "30px" }}
                            >
                              {
                                submissionDetails.descriptions[0]
                                  .applicationName
                              }
                            </p>
                          </div>
                        }
                      </div>
                    </div>
                    {packageName !== "" && (
                      <div className="submit-buttons">
                        <Button
                          className="add-new-product"
                          variant="contained"
                          onClick={() => {
                            console.log(location.state);
                            navigate(
                              `/ui/developer/applications/${appId}/add-app-product`,
                              {
                                state: { teamId: teamId, sellerId: sellerId },
                              }
                            );
                          }}
                        >
                          Add New Product
                        </Button>
                      </div>
                    )}
                  </div>
                )}
            </section>
            <div className="in-app-title-list" style={{ fontWeight: "bolder" }}>
              In-App Products
            </div>
            <div
              className="products-container"
              style={{ marginRight: "6%", marginLeft: "16%" }}
            >
              {packageName === "" && (
                <div className="col-12 blankCls app-list-blank" style={{ marginTop: "3%" }}>
                  <div className="textErrorMsg">
                    Sorry, you cannot create product for this application!
                  </div>
                </div>
              )}
              {productList.length === 0 && (
                <div className="col-12 blankCls app-list-blank">
                  <img
                    src={noProductsImage}
                    alt="App list blank placeholder"
                  />
                  <h3>Welcome to Readylink Marketplace Developer Console!</h3>
                  <div>All your In-App products will be listed here.</div>
                  <div>
                    Start by creating your In-app product application listing.
                  </div>
                  <div>&nbsp;</div>
                </div>
              )}
              {rows.length > 0 && (
                <Box sx={{ width: "100%" }}>
                  <Paper sx={{ width: "100%", mb: 2 }} elevation={0}>
                    <TableContainer component={Paper} elevation={0}>
                      <Table aria-label="collapsible table">
                        <EnhancedTableHead
                          order={order}
                          orderBy={orderBy}
                          onRequestSort={handleRequestSort}
                          rowCount={rows.length}
                        />
                        <TableBody>
                          {visibleRows.map((row, index) => (
                            <Row key={row.id} row={row} goToEditPage={goToEditPage} />
                          ))}
                          {emptyRows > 0 && (
                            <TableRow
                              style={{
                                height: 32 * emptyRows,
                              }}
                            >
                              <TableCell colSpan={6} />
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={rows.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Items per page"
                      sx={{
                        boxShadow: "none",
                        ".MuiTablePagination-root": { border: 0 },
                      }}
                    />
                  </Paper>
                </Box>
              )}
            </div>
          </div>
        </div>
        </Grid>
                </Container>
      }
      {errors?.errorMessage != null && errors?.errorType != null && (
        <ApplicationDetailsErrorDialog />
      )}
      <LoadingBackdrop open={appData.loading || loading} />
    </>
  );
}

export default InAppProducts;